import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuList,
} from "@mui/material";
import {
    EagerIssue,
    EagerLocation,
    Location
} from "../../models";
import {
    getUpdateIssueCallback,
    locationIdToLocationMapByContextSelectorFamily
} from "../../lib/design/document/state/DocumentState";

import CloseIcon from "@mui/icons-material/Close";
import { ContextAwareIdentifier } from "../../lib/design/document/ContextAwareIdentifier";
import { MenuItem } from "@mui/material";
import { ModelType } from "../../lib/design/document/ModelType";
import { StateContext } from "../../lib/design/document/state/StateContext";
import { locationByLocationIdentifierSelectorFamily } from "../../lib/design/document/state/DocumentState";
import { useMemo } from "react";
import { useRecoilValue } from "recoil";
import { useSnackbar } from "notistack";

interface ChangeIssueLocationDialogProps {
    open: boolean;
    onClose: () => void;
    issue: EagerIssue | null;
    stateContext: StateContext;
    locationIds: string[];
}

const IssueLocationMenuItem = ({
    locationId,
    issue,
    stateContext,
    onClose
}: {
    locationId: string;
    issue: EagerIssue | null;
    stateContext: StateContext;
    onClose: () => void;
}) => {
    const locationIdentifier = new ContextAwareIdentifier(
        locationId,
        stateContext,
        ModelType.LOCATION,
    );
    const location: EagerLocation | null = useRecoilValue<EagerLocation | null>(
        locationByLocationIdentifierSelectorFamily(locationIdentifier),
    );
    const snackbar = useSnackbar();
    const updateIssue = getUpdateIssueCallback();

    return (
        <MenuItem
            onClick={async (): Promise<void> => {
                const successMessage = "Group updated";
                const failureMessage = "Failed to update group";

                if (issue) {
                    try {
                        await updateIssue({
                            ...issue,
                            locationId,
                        }, stateContext);
                        snackbar.enqueueSnackbar(successMessage, {
                            variant: "success",
                        });
                    } catch (err) {
                        snackbar.enqueueSnackbar(failureMessage, {
                            variant: "error",
                        });
                    } finally {
                        onClose();
                    }
                } else {
                    snackbar.enqueueSnackbar(failureMessage, {
                        variant: "error",
                    });
                }
            }}
            disabled={location?.id === issue?.locationId}
        >
            {`${location?.name}${location?.id === issue?.locationId ? " (Current Group)" : ""}`}
        </MenuItem>
    );
};

export const ChangeIssueLocationDialog = ({
    open,
    onClose,
    issue,
    stateContext,
    locationIds,
}: ChangeIssueLocationDialogProps) => {
    const locationIdToLocationMap = useRecoilValue<Map<string, Location>>(locationIdToLocationMapByContextSelectorFamily(stateContext));

    const locations = useMemo<Array<Location>>(() => {
        return locationIds
            .map((locationId) => locationIdToLocationMap.get(locationId))
            .filter((location): location is Location => location != undefined);
    }, [locationIds, locationIdToLocationMap]);

    const sortedLocationIds = useMemo<Array<string>>(() => {
        return locations
            .sort((a, b) => {
                if (a.id === issue?.locationId) {
                    return -1;
                }
                if (b.id === issue?.locationId) {
                    return 1;
                }
                if (a.name && b.name) {
                    return a.name.localeCompare(b.name);
                }
                return 0;
            })
            .map((location) => location.id);
    }, [locations]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <IconButton
                size="small"
                onClick={onClose}
                sx={{
                    position: "absolute",
                    right: 4,
                    top: 4,
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogTitle
                sx={{
                    px: 2,
                    py: 1,
                }}
            >
                Select New Group
            </DialogTitle>
            <DialogContent
                sx={{
                    px: 2,
                    py: 1,
                }}
            >
                <MenuList
                    sx={{
                        paddingTop: "0",
                        paddingBottom: "0",
                        maxHeight: "600px",
                        overflowX: "auto"
                    }}
                >
                    {sortedLocationIds.map((locationId) => (
                        <IssueLocationMenuItem
                            key={locationId}
                            locationId={locationId}
                            issue={issue}
                            stateContext={stateContext}
                            onClose={onClose}
                        />
                    ))}
                </MenuList>
            </DialogContent>
        </Dialog>
    );
};